/**
 * Marca de la aplicacion.
 *
 * Los iconos que habia eran un marcador generado durante el desarrollo, no una
 * marca, y en la cabecera solo habia el nombre en texto plano. Esto es una
 * marca tipografica propia, hecha aqui y no traida de ninguna biblioteca: la
 * palabra en la serif de la casa y un signo que funciona a 24 pixeles.
 *
 * El signo es un sello: circulo con filete doble, como el cuño de un registro
 * municipal, y dentro un marcador de mapa. Dice las dos cosas que es la
 * aplicacion, archivo del barrio y recorrido por sus calles, sin dibujar
 * ninguna de las dos de forma literal.
 *
 * Es SVG en linea y no un fichero: escala sin pixelarse, no cuesta una peticion
 * mas en la calle y hereda el color del texto, asi que sirve igual sobre crema
 * que sobre tinta. `currentColor` en todos los trazos es lo que lo permite.
 *
 * Cuando la Federacio facilite su marca oficial, se sustituye AQUI y en el
 * favicon, y no en catorce sitios: ninguna pantalla dibuja el logotipo por su
 * cuenta.
 */

interface Props {
  /** Tamaño del signo en pixeles. El texto acompaña en proporcion. */
  tamano?: number;
  /** Sin texto, solo el sello. Para sitios estrechos. */
  soloSigno?: boolean;
  className?: string;
}

export function Logotipo({ tamano = 32, soloSigno = false, className = '' }: Props) {
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      <Signo tamano={tamano} />
      {soloSigno ? null : (
        <span
          className="font-titular text-[1.375rem] leading-none font-semibold tracking-tight"
          // El nombre propio no se traduce, pero se declara en catalan para que
          // un lector de pantalla en castellano o en ingles lo pronuncie bien.
          lang="ca"
        >
          Recorregut
        </span>
      )}
    </span>
  );
}

/**
 * El sello, aparte, para poder usarlo sin la palabra: en el favicon, en el
 * icono de la aplicacion instalada y en las cabeceras estrechas.
 *
 * `aria-hidden`: el nombre ya viaja en el texto de al lado o en el rotulo del
 * enlace que lo contiene. Anunciarlo dos veces es ruido.
 */
export function Signo({ tamano = 32 }: { tamano?: number }) {
  return (
    <svg
      width={tamano}
      height={tamano}
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden="true"
      focusable="false"
      className="shrink-0"
    >
      {/* Filete exterior del sello */}
      <circle cx="16" cy="16" r="15" stroke="currentColor" strokeWidth="1.25" />
      {/* Filete interior: el doble aro es lo que lo hace cuño y no boton */}
      <circle cx="16" cy="16" r="12.25" stroke="currentColor" strokeWidth="0.75" opacity="0.55" />
      {/*
        Marcador de mapa: gota con el vertice abajo y el hueco del centro
        vaciado, para que a tamaño pequeño no se cierre en una mancha.
      */}
      <path
        d="M16 8.5c-3.05 0-5.5 2.4-5.5 5.35 0 3.9 4.55 8.6 5.16 9.22a.48.48 0 0 0 .68 0c.61-.62 5.16-5.32 5.16-9.22 0-2.95-2.45-5.35-5.5-5.35Z"
        fill="currentColor"
      />
      <circle cx="16" cy="13.85" r="1.95" fill="var(--color-crema-50, #FBF7F0)" />
    </svg>
  );
}
